import { Component, h, register } from './core.js';
import { renderIcon } from './icons.js';

/**
 * Dictation: speech to text over the browser's SpeechRecognition API, with a
 * microphone button, a silence timer and a transcript. Works headless too
 * (button: false) for hosts that have their own control.
 *
 * new dfc.Dictation(container, {
 *   lang: navigator.language, continuous: true, interimResults: true,
 *   silenceMs: 2000 (auto-stop after this much quiet; 0 = never), maxMs: 60000 (hard stop; 0 = never),
 *   button: true, size: 'md' | 'sm' | 'lg', label: null, showTranscript: false, disabled: false
 * })
 * Events: start, result { transcript, interim, final, isFinal }, stop { transcript, reason: 'user'|'silence'|'max'|'end' },
 *         error { code, message }, unsupported, valueChanged { value: transcript, previous, source, isFinal }
 * Methods: start(), stop(), abort(), toggle(), listening (getter), transcript (getter), reset()
 * Static: dfc.Dictation.supported
 */
export class Dictation extends Component {
	static get componentName() { return 'dictation'; }

	static get supported() {
		return typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
	}

	defaults() {
		return { value: '', lang: (typeof navigator !== 'undefined' && navigator.language) || 'en-GB', continuous: true, interimResults: true, silenceMs: 2000, maxMs: 60000, button: true, size: 'md', label: null, showTranscript: false, disabled: false, startTitle: 'Start dictation', stopTitle: 'Stop dictation' };
	}

	render() {
		const o = this.options;
		if (!this._valueApplied) { this._final = o.value ? String(o.value) : ''; this._valueApplied = true; }
		this._final = this._final || '';
		this._interim = this._interim || '';
		this._listening = !!this._listening;
		this.el.className = `dfc dfc-dictation dfc-size-${o.size}` + (this._listening ? ' is-listening' : '') + (o.disabled || !Dictation.supported ? ' is-disabled' : '') + (o.className ? ' ' + o.className : '');
		this.el.innerHTML = '';
		if (o.button) {
			this.button = h('button', {
				type: 'button', className: 'dfc-dictation-button', 'aria-pressed': this._listening ? 'true' : 'false',
				title: Dictation.supported ? (this._listening ? o.stopTitle : o.startTitle) : 'Speech recognition is not available in this browser',
				'aria-label': this._listening ? o.stopTitle : o.startTitle, disabled: o.disabled || !Dictation.supported ? true : null,
			}, renderIcon(this._listening ? 'stop' : 'microphone'), h('span', { className: 'dfc-dictation-pulse', 'aria-hidden': 'true' }));
			this.el.appendChild(this.button);
			if (o.label) this.el.appendChild(h('span', { className: 'dfc-dictation-label', text: o.label }));
		}
		if (o.showTranscript) {
			this.transcriptEl = h('div', { className: 'dfc-dictation-transcript', 'aria-live': 'polite' });
			this._paintTranscript();
			this.el.appendChild(this.transcriptEl);
		}
	}

	bind() {
		this._onClick = (e) => { if (e.target.closest && e.target.closest('.dfc-dictation-button')) this.toggle(); };
		this.el.addEventListener('click', this._onClick);
	}

	unbind() {
		if (this.el) this.el.removeEventListener('click', this._onClick);
		this.abort();
	}

	get listening() { return this._listening; }
	get transcript() { return (this._final + (this._interim ? ' ' + this._interim : '')).trim(); }
	/** value = the transcript. Setting it replaces the final text. */
	get value() { return this.transcript; }
	set value(v) { const prev = this.transcript; this._final = v === undefined || v === null ? '' : String(v); this._interim = ''; this._paintTranscript(); this._valueChanged(this.transcript, prev, 'api'); }

	reset() { this.value = ''; return this; }

	toggle() { return this._listening ? this.stop('user') : this.start(); }

	start() {
		const o = this.options;
		if (this._listening || o.disabled) return this;
		const Ctor = typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
		if (!Ctor) { this.emit('unsupported', {}); return this; }
		const rec = new Ctor();
		rec.lang = o.lang; rec.continuous = !!o.continuous; rec.interimResults = !!o.interimResults;
		rec.onstart = () => { this._listening = true; this._stopReason = null; this.render(); this._armSilence(); if (o.maxMs) this._maxTimer = setTimeout(() => this.stop('max'), o.maxMs); this.emit('start', {}); };
		rec.onresult = (ev) => {
			let interim = '';
			for (let i = ev.resultIndex; i < ev.results.length; i++) {
				const text = ev.results[i][0].transcript;
				if (ev.results[i].isFinal) this._final = (this._final + ' ' + text).trim(); else interim += text;
			}
			const prev = this._lastTranscript || '';
			this._interim = interim.trim();
			this._paintTranscript();
			this._armSilence();
			this.emit('result', { transcript: this.transcript, interim: this._interim, final: this._final, isFinal: !this._interim });
			this._lastTranscript = this.transcript;
			this._valueChanged(this.transcript, prev, 'user', { isFinal: !this._interim });
		};
		rec.onerror = (ev) => {
			const code = ev.error || 'unknown';
			if (code === 'no-speech' || code === 'aborted') return; // handled by onend / silence
			this.emit('error', { code, message: Dictation._errorText(code) });
		};
		rec.onend = () => {
			this._clearTimers();
			const wasListening = this._listening;
			this._listening = false; this._rec = null;
			this.render();
			if (wasListening) this.emit('stop', { transcript: this.transcript, reason: this._stopReason || 'end' });
		};
		this._rec = rec;
		try { rec.start(); } catch (e) { this.emit('error', { code: 'start-failed', message: e.message }); }
		return this;
	}

	stop(reason) {
		if (!this._rec) return this;
		this._stopReason = reason || 'user';
		try { this._rec.stop(); } catch (e) { /* already stopped */ }
		return this;
	}

	abort() {
		this._clearTimers();
		if (this._rec) { try { this._rec.onend = null; this._rec.abort(); } catch (e) { /* ignore */ } this._rec = null; }
		if (this._listening) { this._listening = false; if (this.el) this.render(); }
		return this;
	}

	_armSilence() {
		clearTimeout(this._silenceTimer);
		if (this.options.silenceMs) this._silenceTimer = setTimeout(() => this.stop('silence'), this.options.silenceMs);
	}
	_clearTimers() { clearTimeout(this._silenceTimer); clearTimeout(this._maxTimer); }
	_paintTranscript() {
		if (!this.transcriptEl) return;
		this.transcriptEl.innerHTML = '';
		if (this._final) this.transcriptEl.appendChild(h('span', { className: 'dfc-dictation-final', text: this._final }));
		if (this._interim) this.transcriptEl.appendChild(h('span', { className: 'dfc-dictation-interim', text: ' ' + this._interim }));
		if (!this._final && !this._interim) this.transcriptEl.appendChild(h('span', { className: 'dfc-dictation-placeholder', text: this._listening ? 'Listening…' : '' }));
	}
	static _errorText(code) {
		return { 'not-allowed': 'Microphone access was refused.', 'service-not-allowed': 'Speech recognition is not allowed here.', 'audio-capture': 'No microphone was found.', 'network': 'Speech recognition needs a network connection.', 'language-not-supported': 'That language is not supported.' }[code] || 'Speech recognition failed (' + code + ').';
	}
}

register('dictation', Dictation);
